import { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Rocket, GraduationCap, Award, Map, FileText, BookOpen, Library,
  CheckCircle2, Send, Zap, DollarSign, Repeat, CheckSquare, Clock,
  Users, Sparkles, Landmark, BarChart2, Trophy, Lock, Bell, BellOff
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  'rocket': Rocket, 'graduation-cap': GraduationCap, 'award': Award, 'map': Map,
  'file-text': FileText, 'book-open': BookOpen, 'library': Library,
  'check-circle': CheckCircle2, 'send': Send, 'zap': Zap, 'dollar-sign': DollarSign,
  'repeat': Repeat, 'check-square': CheckSquare, 'clock': Clock, 'users': Users,
  'sparkles': Sparkles, 'landmark': Landmark, 'bar-chart-2': BarChart2,
};

export default function Achievements() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);

  const fetchAchievements = useCallback(async () => {
    if (!wsId) return;
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/achievements`);
      if (res.ok) {
        const d = await res.json();
        setData(d);
        setDisabled(d.badges_disabled);
        // Show toast for newly earned badges
        if (d.newly_earned?.length > 0 && !d.badges_disabled) {
          d.newly_earned.forEach(id => {
            const badge = d.badges.find(b => b.id === id);
            if (badge) toast.success(`Badge Earned: ${badge.title}!`, { description: badge.description });
          });
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [wsId]);

  useEffect(() => { fetchAchievements(); }, [fetchAchievements]);

  const toggleBadges = async (val) => {
    setDisabled(val);
    await apiFetch(`${API_URL}/api/workspaces/${wsId}/achievements/preferences`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ badges_disabled: val })
    });
    toast.success(val ? 'Badges disabled' : 'Badges enabled');
  };

  if (loading) return <div className="space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}</div>;

  // Group badges by category
  const categories = {};
  (data?.badges || []).forEach(b => {
    if (!categories[b.category]) categories[b.category] = [];
    categories[b.category].push(b);
  });

  const earned = data?.earned || 0;
  const total = data?.total || 0;
  const pct = total > 0 ? Math.round((earned / total) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="achievements-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
            <Trophy className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="achievements-heading">Achievements</h1>
            <p className="text-sm text-muted-foreground">Earn badges as you master CountBucks</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {disabled ? <BellOff className="w-4 h-4 text-muted-foreground" /> : <Bell className="w-4 h-4 text-amber-500" />}
            <Label htmlFor="badge-toggle" className="text-sm text-muted-foreground cursor-pointer">
              {disabled ? 'Badges off' : 'Badges on'}
            </Label>
            <Switch
              id="badge-toggle"
              checked={!disabled}
              onCheckedChange={(v) => toggleBadges(!v)}
              data-testid="badge-toggle"
            />
          </div>
        </div>
      </div>

      {/* Progress */}
      <Card className={cn("bg-gradient-to-r from-amber-50 via-background to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20", disabled && "opacity-50")}>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">{earned} of {total} badges earned</span>
            <span className="text-sm font-bold" style={{ color: pct === 100 ? '#10b981' : '#f59e0b' }}>{pct}%</span>
          </div>
          <Progress value={pct} className="h-2" />
        </CardContent>
      </Card>

      {disabled && (
        <Card className="border-amber-500/30 bg-amber-50/50 dark:bg-amber-950/10">
          <CardContent className="pt-5 pb-4 flex items-center gap-3">
            <BellOff className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium">Badges are currently disabled</p>
              <p className="text-xs text-muted-foreground">You won't receive badge notifications. Toggle the switch above to re-enable.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Badge Categories */}
      {Object.entries(categories).map(([catName, badges]) => (
        <div key={catName} className={cn("space-y-3", disabled && "opacity-40")}>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{catName}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {badges.map((badge) => {
              const Icon = ICON_MAP[badge.icon] || Trophy;
              return (
                <Card
                  key={badge.id}
                  className={cn(
                    "transition-all",
                    badge.earned
                      ? "border-amber-500/30 bg-gradient-to-br from-amber-50/50 to-background dark:from-amber-950/10"
                      : "opacity-50"
                  )}
                  data-testid={`badge-${badge.id}`}
                >
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-start gap-3">
                      <div
                        className={cn(
                          "w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all",
                          badge.earned ? "shadow-md" : "grayscale"
                        )}
                        style={{ backgroundColor: badge.earned ? badge.color + '20' : undefined }}
                      >
                        {badge.earned ? (
                          <Icon className="w-5 h-5" style={{ color: badge.color }} />
                        ) : (
                          <Lock className="w-5 h-5 text-muted-foreground/50" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className={cn("font-semibold text-sm", !badge.earned && "text-muted-foreground")}>{badge.title}</h3>
                          {badge.earned && <CheckCircle2 className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{badge.description}</p>
                        {badge.earned && badge.earned_at && (
                          <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1 font-medium">
                            Earned {new Date(badge.earned_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
